/**
 * Derives the 1-2 letter avatar initials for a chat's OWNER, from
 * whatever label the caller has for them — a resolved display name
 * ("Jane Doe"), a bare email, or a raw user id when nothing else
 * resolved.
 *
 * OR-6455: the internal read-only chat viewer must show the message
 * OWNER's initials, not the logged-in viewer's — `MessageBubble`
 * otherwise derives the avatar from `useAuth()`, which is correct for
 * a live chat (viewer === sender) but wrong here. Mirrors
 * `core-auth`'s `getInitials(firstName, lastName)` in spirit, but
 * works off a single flat label string since that's all
 * `ownerDisplayName` ever carries — there's no separate first/last
 * name field for a chat's owner on this surface.
 */
export const getOwnerAvatarInitials = (label: string | undefined): string => {
  if (!label) return '';
  const trimmed = label.trim();
  if (!trimmed) return '';

  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  }
  // Single "word". When the display-name lookup misses, the caller falls
  // back to the raw Auth0 subject id (`auth0|65f3c2a1...`,
  // `google-oauth2|1098...`) — matching alnum against the WHOLE string
  // would stop at the '|' and return the shared connection name
  // ('auth0'/'google-oauth2') for every owner on that connection, a
  // visual identity collision between different real people. Strip a
  // `provider|` prefix first so the initials come from the actual
  // per-user id portion instead.
  const idPart = trimmed.includes('|') ? trimmed.slice(trimmed.lastIndexOf('|') + 1) : trimmed;
  // First two alphanumeric characters (skips a leading symbol like a
  // stray '@' or punctuation) rather than just the first character.
  const alnum = idPart.match(/[a-zA-Z0-9]+/)?.[0] ?? '';
  return alnum.slice(0, 2).toUpperCase();
};
